import { extend } from "@dhx/ts-common/core";
import {
	DataCollection,
	dragManager,
	DataEvents,
	DragEvents,
	IDataEventsHandlersMap,
	IDragEventsHandlersMap,
	IDragInfo,
} from "@dhx/ts-data";
import { create, el, VNode } from "@dhx/ts-common/dom";
import { IEventSystem } from "@dhx/ts-common/events";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";
import { IHandlers, SelectionEvents, ITouchParam, anyFunction, Id } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import { Selection } from "./Selection";
import { locate, locateNode, eventHandler } from "@dhx/ts-common/html";
import { IList, IListConfig, IListEventHandlersMap, IListItem, ISelection, ListEvents } from "./types";
import { getEditor } from "./editors/editors";

export const MOVE_UP = 1;
export const MOVE_DOWN = 2;

export class List extends View implements IList {
	public config: IListConfig;
	public data: DataCollection;
	public events: IEventSystem<
		DataEvents | ListEvents | DragEvents,
		IListEventHandlersMap & IDataEventsHandlersMap & IDragEventsHandlersMap
	>;
	public selection: ISelection;
	public keyManager: IKeyManager;

	protected _handlers: IHandlers;
	protected _focus: Id;
	protected _edited: Id;
	protected _events: IHandlers;

	private _topOffset: number;
	private _visibleHeight: number;
	private _touch: ITouchParam;
	protected _changed: boolean;

	constructor(node: HTMLElement | string, config: IListConfig = {}) {
		let itemHeight: number | string = config.itemHeight || (config.virtual ? 37 : null);
		if (itemHeight && typeof itemHeight === "number") {
			itemHeight = itemHeight.toString() + "px";
		}

		super(
			node,
			extend(
				{
					itemHeight: itemHeight,
					keyNavigation: true,
					editable: false,
					selection: true,
					htmlEnable: true,
				},
				config
			)
		);

		this._touch = {
			duration: 350,
			dblDuration: 300,
			timer: null,
			start: false,
			timeStamp: null,
		};

		// init data colleciton and link events between widget and data
		const data = this.config.data;
		if (!(data instanceof DataCollection)) {
			this.data = new DataCollection({});
			this.events = this.data.events;
			if (data) this.data.parse(data);
		} else {
			// [TODO] deprecate in favor of raw data input
			this.data = data;
			this.events = data.events;
		}

		this.selection = new Selection(
			{
				disabled: !this.config.selection,
				multiselection: this.config.multiselection,
			},
			this.data,
			this.events
		);

		if (this.config.keyNavigation) {
			this.keyManager = new KeyManager((e: KeyboardEvent, focusId: Id) => {
				return focusId == this._uid && (!this._edited || (this._edited && e.key !== "escape"));
			});
			this._initHotKey();
		}

		this.events.on(DataEvents.change, (_, status, item) => {
			if (status === "setPage") {
				this.showItem(this.data.getId(item[0]));
			}
			this.paint();
		});

		this.events.on(SelectionEvents.afterUnSelect, () => this.paint());
		this.events.on(SelectionEvents.afterSelect, (id: Id) => {
			if (id && this.config.selection) this._focus = id;
			this.paint();
		});
		this.events.on(ListEvents.afterEditEnd, this.editEnd.bind(this));

		const drop = (value: boolean) => (info: IDragInfo) => {
			this.data.setMeta(this.data.getItem(info.target), "drop", value);
			this.paint();
		};
		this.events.on(DragEvents.canDrop, drop(true));
		this.events.on(DragEvents.cancelDrop, drop(false));

		const drag = (value: boolean) => (info: IDragInfo) => {
			info.source.map(id => this.data.setMeta(this.data.getItem(id), "drag", value));
			this.paint();
		};
		this.events.on(DragEvents.dragStart, drag(true));
		this.events.on(DragEvents.afterDrag, drag(false));

		this._handlers = {
			onmousedown: (e: MouseEvent) => {
				this._dragStart(e);
			},
			ontouchstart: (e: TouchEvent) => {
				this._touch.timer = setTimeout(() => {
					this._dragStart(e);
				}, this._touch.duration);

				if (!this._touch.timeStamp) {
					this._touch.timeStamp = +e.timeStamp.toFixed();
				} else {
					const dblTap = this._touch.dblDuration >= this._touch.timeStamp - +e.timeStamp.toFixed();
					if (dblTap) {
						e.preventDefault();
						this._dblClick(e);
					}
					this._touch.timeStamp = null;
				}
				setTimeout(() => {
					this._touch.timeStamp = null;
				}, this._touch.dblDuration);
			},
			ontouchmove: (e: TouchEvent) => {
				this._touch.start && e.preventDefault();
				this._clearTouchTimer();
			},
			ontouchend: () => {
				this._touch.start = false;
				this._clearTouchTimer();
			},
			ondragstart: () => (this.config.dragMode && !this._edited ? false : null),
			oncontextmenu: (e: MouseEvent) => {
				const id: Id = this.data.getItem(locate(e))?.id as Id;
				if (!id) {
					return;
				}
				this.events.fire(ListEvents.itemRightClick, [id, e]);
			},
			onclick: (e: MouseEvent) => {
				const id: Id = this.data.getItem(locate(e))?.id as Id;
				if (!id) {
					return;
				}
				this.selection.add(id, e.ctrlKey || e.metaKey, e.shiftKey);
				if (this.config.selection) {
					this._focus = id;
				}
				this.events.fire(ListEvents.click, [id, e]);
				this.paint();
			},
			ondblclick: (e: MouseEvent) => {
				this._dblClick(e);
			},
			onscroll: (e: any) => {
				if (this.config.virtual) {
					this._topOffset = e.target.scrollTop;
					this._visibleHeight = e.target.offsetHeight;
					this.paint();
				}
			},
			onmouseover: (e: MouseEvent) => {
				const id: Id = this.data.getItem(locate(e))?.id as Id;
				if (!id) {
					return;
				}
				const prevId = locate(e.relatedTarget as HTMLElement);
				if (id !== prevId) this.events.fire(ListEvents.itemMouseOver, [id, e]);
			},
		};

		const evs = this.config.eventHandlers;
		if (evs) {
			for (const [event_name, events] of Object.entries(evs)) {
				this._handlers[event_name] = eventHandler(
					e => locate(e),
					events,
					this._handlers[event_name] as anyFunction
				);
			}
		}

		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
		}

		// defaults for virtual rendering
		this._topOffset = this._visibleHeight = 0;

		const view = create({
			render: () => this._renderList(),
			hooks: {
				didMount: (vm: any) => {
					if (this.config.virtual) this._visibleHeight = vm.node.el.offsetHeight;
				},
				didRedraw: vm => this._didRedraw(vm),
			},
		});
		this.mount(node, view);
	}

	protected _didRedraw(vm: any) {
		// do nothing
	}

	private _dblClick(e: MouseEvent | TouchEvent) {
		let id: Id = locate(e);
		if (!id) {
			return;
		}
		id = this.data.getItem(id).id;
		if (this.config.editable) {
			this.editItem(id);
		}
		this.events.fire(ListEvents.doubleClick, [id, e]);
	}

	private _clearTouchTimer(): void {
		if (this._touch.timer) {
			clearTimeout(this._touch.timer);
			this._touch.timer = null;
		}
	}

	private _dragStart(e: TouchEvent | MouseEvent): void {
		this._touch.start = true;
		const itemsForGhost = [];
		const item = locateNode(e, "data-dhx-id") as HTMLElement;
		const itemId = item && item.getAttribute("data-dhx-id");
		let selectionIds = this.selection.getId();
		if (this.config.multiselection && selectionIds instanceof Array) {
			selectionIds.map(id => {
				if (id !== itemId && this.getRootView().refs[id]) {
					itemsForGhost.push(this.getRootView().refs[id].el);
				}
			});

			selectionIds = [...selectionIds];
		}
		if (typeof selectionIds === "string") {
			selectionIds = [selectionIds];
		}
		return this.config.dragMode && !this._edited
			? dragManager.onMouseDown(e, (selectionIds as Id[]) || [itemId], itemsForGhost)
			: null;
	}

	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	disableSelection() {
		this.selection.disable();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	enableSelection() {
		this.selection.enable();
	}

	editItem(id: Id): void {
		this._edited = id;
		if (!this.data.getItem(this._edited) || !this.events.fire(ListEvents.beforeEditStart, [id])) {
			this._edited = null;
			return;
		}
		this.paint();
		this.events.fire(ListEvents.afterEditStart, [id]);
	}
	// [TODO] maybe better name
	editEnd(value: any, id?: Id) {
		if (this._edited) {
			if (value !== null) {
				const item = this.data.getItem(id);
				this.data.update(id, { ...item, value });
				this._changed = true;
			}
			this._edited = null;
			this.paint();
		}
	}

	/*
		Focus API allows to mark item as active one, but not selected yet
		Used by keyboard navigation
	*/

	getFocusItem(): any {
		return this.data.getItem(this._focus);
	}

	setFocus(id: Id): void {
		if (this._focus != id && this.data.exists(id)) {
			this._focus = id;
			this.showItem(id);
			this.events.fire(ListEvents.focusChange, [this.data.getIndex(this._focus), this._focus]);
			this.paint();
		}
	}

	getFocus(): Id {
		return this._focus;
	}

	destructor() {
		this.events && this.events.clear();
		this.keyManager && this.keyManager.destructor();
		this.selection && this.selection.destructor();

		this.config = this.events = this.selection = this.keyManager = null;
		this._handlers = this._focus = this._edited = this._events = this._topOffset = this._visibleHeight = this._touch = null;
		this.unmount();
	}

	showItem(id: Id): void {
		const rootView = this.getRootView();
		if (!rootView || !rootView.node || !rootView.node.el || typeof id === "undefined") {
			return;
		}

		const listEl: HTMLElement = this.getRootNode();
		if (!listEl) {
			return;
		}

		const virtual = this.config.virtual;
		const index = this.data.getIndex(id);
		const currentPage = Math.floor(index / listEl.children.length) || 0;
		const el = listEl.children[index - listEl.children.length * currentPage] as HTMLElement;

		if (!virtual && !el) return;
		const height = virtual ? parseInt(this.config.itemHeight as string) : el.clientHeight;
		const top = virtual ? index * height : el.offsetTop;

		if (top >= listEl.scrollTop + listEl.clientHeight - height) {
			listEl.scrollTo(0, top - listEl.clientHeight + height);
		} else if (top < listEl.scrollTop) {
			listEl.scrollTo(0, top);
		}
	}

	protected _renderItem(item: IListItem, index: number): VNode {
		const { itemHeight } = this.config;
		if (item.$empty) {
			return el("li", {
				class: "dhx_list-item",
				style: {
					height: itemHeight,
				},
			});
		}

		const html = (this.config.template && this.config.template(item)) || item.html;
		const focus = item.id == this._focus;

		if (item.id == this._edited) {
			const editor = getEditor(item, this);
			return editor.toHTML();
		}

		const mx = this.data.getMetaMap(item);
		const node = {
			...this._events,
			class:
				"dhx_list-item" +
				(mx && mx.selected ? " dhx_list-item--selected" : "") +
				(focus ? " dhx_list-item--focus" : "") +
				(mx && mx.drop && !this._edited ? " dhx_list-item--drophere" : "") +
				(mx && mx.drag && !this._edited ? " dhx_list-item--dragtarget" : "") +
				(this.config.dragMode && !this._edited ? " dhx_list-item--drag" : "") +
				(item.css ? " " + item.css : ""),
			"data-dhx-id": item.id,
			_ref: item.id.toString(),
			style: {
				height: itemHeight,
			},
			_key: item.id,
			// ".innerHTML": html,
			...this.getItemAriaAttrs(this, item),
			tabindex: focus ? 0 : -1,
		};

		if (html) {
			if (html === item.html || this.config.htmlEnable) {
				node[".innerHTML"] = html;
				return el("li", node);
			} else {
				return el("li", node, html);
			}
		} else {
			const value = item.text || item.value;
			if (this.config.htmlEnable) {
				node[".innerHTML"] = value;
			} else node.class += " dhx_list-item--text";
			return this.config.htmlEnable ? el("li", node) : el("li", node, value);
		}
	}

	protected _renderList(): VNode {
		const range = this._getRange();
		// mapRange
		const data = this.data.getRawData(range[0], range[1]);
		let kids = data.map((obj, index) => this._renderItem(obj, index));

		if (this.config.virtual) {
			kids = [
				el(".div", { style: { height: range[2] + "px" } }),
				...kids,
				el(".div", { style: { height: range[3] + "px" } }),
			];
		}

		return el(
			"ul.dhx_widget.dhx_list",
			{
				style: {
					"max-height": this.config.height,
					position: "relative",
				},
				tabindex: 0,
				class:
					(this.config.css ? this.config.css : "") +
					(this.config.multiselection && this.selection.getItem() ? " dhx_no-select--pointer" : ""),
				"data-dhx-widget-id": this._uid,
				...this._handlers,
				...this._getListAriaAttrs(this.config, this.data.getLength()),
			},
			kids
		);
	}

	moveFocus(mode: number, step?: number): void {
		const length = this.data.getLength();
		if (!length) {
			return;
		}

		let id = this._focus;
		const index = id ? this.data.getIndex(id) : -1;

		step = step || 1;
		if (mode === MOVE_DOWN) {
			id = this.data.getId(Math.min(index + step, length - 1));
		} else if (mode === MOVE_UP) {
			id = this.data.getId(Math.max(index - step, 0));
		}

		this.setFocus(id);
	}

	protected _getRange(): [number, number, number, number] {
		if (this.config.virtual) {
			const overscanCount = 5;
			const visibleHeight = this._visibleHeight || parseInt(this.config.height as string);
			const itemHeight = parseInt(this.config.itemHeight as string);
			const total = this.data.getRawData(0, -1).length;
			const totalHeight = total * itemHeight;
			let position = this._topOffset;

			// correct value to be in [0, total-visible] range
			position = Math.max(0, Math.min(position, totalHeight - visibleHeight));
			const index = Math.floor(position / itemHeight);
			const count = Math.min(total - index, Math.floor(visibleHeight / itemHeight) + overscanCount);

			this._topOffset = position;
			return [index, count + index, index * itemHeight, totalHeight - itemHeight * (count + index)];
		} else {
			return [0, -1, 0, 0];
		}
	}

	protected _getHotkeys(): IHandlers {
		return {
			arrowDown: e => {
				this.moveFocus(MOVE_DOWN);
				if (this._changed) this._changed = false;
				e.preventDefault();
			},
			arrowUp: e => {
				this.moveFocus(MOVE_UP);
				if (this._changed) this._changed = false;
				e.preventDefault();
			},
			escape: () => {
				this.editEnd(null);
				if (this._changed) this._changed = false;
			},
			enter: (e: KeyboardEvent) => {
				if (this._changed) {
					this._changed = false;
					return;
				}
				const selected = this.selection.getItem();
				const selectedId = selected instanceof Array ? selected[0]?.id : selected?.id;
				if (
					this.config.editable &&
					!this._edited &&
					((selected && selectedId === this._focus) || !selected)
				) {
					this.editItem(this._focus);
				} else this.selection.add(this._focus);
				this.events.fire(ListEvents.click, [this._focus, e]);
			},
			"shift+enter": (e: KeyboardEvent) => {
				this.selection.add(this._focus, false, true);
				this.events.fire(ListEvents.click, [this._focus, e]);
			},
			"ctrl+enter": (e: KeyboardEvent) => {
				this.selection.add(this._focus, true, false);
				this.events.fire(ListEvents.click, [this._focus, e]);
			},
			"ctrl+a": (e: KeyboardEvent) => {
				if (this.config.multiselection) {
					e.preventDefault();
					this.selection.remove();
					const ids = this.data.map(i => i.id);
					ids.forEach(id => {
						if (this.config.multiselection === "ctrlClick") {
							this.selection.add(id, true);
						} else {
							this.selection.add(id);
						}
					});
				}
			},
		};
	}

	private _initHotKey() {
		const handlers = this._getHotkeys();

		for (const key in handlers) {
			this.keyManager.addHotKey(key, handlers[key]);
		}

		for (const key in this.config.hotkeys) {
			this.keyManager.addHotKey(key, this.config.hotkeys[key]);
		}
	}

	private getItemAriaAttrs(context: this, item: IListItem) {
		const getAriaGrabbed = (context, item) =>
			context.config.dragMode && !context._edited
				? { "aria-grabbed": Boolean(item.$dragtarget && !context._edited).toString() }
				: {};

		const getAriaRoleDescription = context =>
			context.config.editable
				? {
						"aria-roledescription": `${
							context._edited ? "Press Enter to stop editing" : "Double click to edit content"
						}`,
				  }
				: {};

		return {
			role: "option",
			"aria-selected": item.$selected ? "true" : "false",
			...getAriaGrabbed(context, item),
			...getAriaRoleDescription(context),
		};
	}

	protected _getListAriaAttrs(config, dataLength) {
		return {
			role: "listbox",
			"aria-label": `Listbox ${config.title || ""}, count of options = ${dataLength}.${
				config.editable ? " Content is editable." : ""
			}`,
			"aria-multiselectable": config.selection && config.multiselection ? "true" : "false",
			"aria-readonly": config.editable ? "false" : "true",
		};
	}
}
